import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;
const DESKTOP_MEDIA_QUERY = '(min-width: 750px)';
const VIEWPORT_EDGE_PADDING = 16;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();
  #desktopMediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
  #layoutObserver = new ResizeObserver(() => this.#debouncedPositionAllTextOnlySubmenus());
  #debouncedPositionAllTextOnlySubmenus = debounce(() => this.#positionAllTextOnlySubmenus(), 100);

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    this.#layoutObserver.observe(this);
    this.refs.overflowMenu?.addEventListener('reflow', this.#debouncedPositionAllTextOnlySubmenus, {
      signal: this.#abortController.signal,
    });
    this.#desktopMediaQuery.addEventListener('change', this.#debouncedPositionAllTextOnlySubmenus, {
      signal: this.#abortController.signal,
    });
    window.addEventListener('resize', this.#debouncedPositionAllTextOnlySubmenus, {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(() => {
      this.#preloadImages();
      this.#schedulePositionAllTextOnlySubmenus();
    });

    document.fonts?.ready.then(() => this.#schedulePositionAllTextOnlySubmenus());
    this.#schedulePositionAllTextOnlySubmenus();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#layoutObserver.disconnect();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // For text-only submenus, ensure they are positioned (or at least unhidden)
    // right when opening. This prevents template/page-specific timing issues
    // where initial positioning runs too early and submenu stays hidden.
    if (submenu?.classList.contains('menu-list__submenu--text-only')) {
      const positioned = positionTextOnlySubmenu(item, submenu);
      if (!positioned) {
        submenu.style.setProperty('--submenu-content-offset-x', '0px');
      }
      submenu.setAttribute('data-submenu-positioned', '');
    }

    // Mark submenu as active for content-visibility optimization
    if (submenu) {
      submenu.dataset.active = '';
    }

    // Start the opacity animation immediately regardless of height measurement
    this.style.setProperty('--submenu-opacity', '1');

    // Measure submenu height. During View Transitions (pageswap/pagereveal) the
    // browser may not have finalised layout yet, causing offsetHeight to return 0.
    // If that happens, retry after the next paint — by then layout is stable.
    const setHeight = (retries = 3) => {
      const submenuHeight = submenu ? submenu.offsetHeight : 0;
      if (submenuHeight > 0) {
        this.style.setProperty('--submenu-height', `${submenuHeight}px`);
      } else if (retries > 0 && this.#state.activeItem === item) {
        // Layout not ready yet — retry on next animation frame
        requestAnimationFrame(() => setHeight(retries - 1));
      }
    };
    setHeight();
  };

  #schedulePositionAllTextOnlySubmenus() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#positionAllTextOnlySubmenus());
    });
  }

  #positionAllTextOnlySubmenus() {
    if (!window.matchMedia(DESKTOP_MEDIA_QUERY).matches) {
      clearAllTextOnlySubmenuPositions(this);
      return;
    }

    const submenus = this.querySelectorAll('.menu-list__submenu--text-only');
    submenus.forEach((submenu) => {
      if (submenu instanceof HTMLElement) {
        submenu.setAttribute('data-measuring', '');
      }
    });

    let needsRetry = false;
    const listItems = this.querySelectorAll('.menu-list__list-item:not([slot="overflow"])');

    for (const listItem of listItems) {
      const link = listItem.querySelector('[ref="menuitem"]');
      const submenu = listItem.querySelector('[ref="submenu[]"]');

      if (!(link instanceof HTMLElement) || !(submenu instanceof HTMLElement)) continue;
      if (!submenu.classList.contains('menu-list__submenu--text-only')) continue;

      const positioned = positionTextOnlySubmenu(link, submenu);
      if (!positioned) needsRetry = true;
    }

    submenus.forEach((submenu) => {
      if (submenu instanceof HTMLElement) {
        submenu.removeAttribute('data-measuring');
      }
    });

    if (needsRetry) {
      requestAnimationFrame(() => this.#positionAllTextOnlySubmenus());
    }
  }

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
      // Remove active state from submenu after animation completes
      if (submenu) {
        delete submenu.dataset.active;
      }
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const listItem = element.closest('.menu-list__list-item');
  if (!(listItem instanceof HTMLElement)) return null;

  if (listItem.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(listItem.parentElement?.querySelector('[slot="overflow"]'));
  }

  return /** @type {HTMLElement | null} */ (listItem.querySelector('[ref="menuitem"]'));
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * @param {ParentNode} root
 */
function clearAllTextOnlySubmenuPositions(root) {
  root.querySelectorAll('.menu-list__submenu--text-only').forEach((submenu) => {
    if (submenu instanceof HTMLElement) {
      clearTextOnlySubmenuPosition(submenu);
    }
  });
}

/**
 * Anchor text-only submenu content under its parent top-level link on desktop.
 * @param {HTMLElement} link
 * @param {HTMLElement} submenu
 * @returns {boolean} Whether positioning succeeded
 */
function positionTextOnlySubmenu(link, submenu) {
  if (!submenu.classList.contains('menu-list__submenu--text-only')) return false;

  const submenuRect = submenu.getBoundingClientRect();
  const linkRect = link.getBoundingClientRect();
  let offsetX = linkRect.left - submenuRect.left;

  const inner = submenu.querySelector('.menu-list__submenu-inner');
  if (!(inner instanceof HTMLElement)) return false;

  const innerWidth = inner.offsetWidth;
  if (innerWidth === 0 || linkRect.width === 0) return false;

  offsetX = Math.max(0, Math.min(offsetX, submenuRect.width - innerWidth));

  const contentRight = submenuRect.left + offsetX + innerWidth;
  const maxRight = window.innerWidth - VIEWPORT_EDGE_PADDING;
  if (contentRight > maxRight) {
    offsetX -= contentRight - maxRight;
  }

  offsetX = Math.max(0, offsetX);

  submenu.style.setProperty('--submenu-content-offset-x', `${Math.round(offsetX)}px`);
  submenu.setAttribute('data-submenu-positioned', '');
  return true;
}

/**
 * @param {HTMLElement} submenu
 */
function clearTextOnlySubmenuPosition(submenu) {
  submenu.style.removeProperty('--submenu-content-offset-x');
  submenu.removeAttribute('data-submenu-positioned');
}
